/**
 * Fixed-point integer arithmetic for combat math.
 *
 * # Why fixed-point?
 *
 * Real-time combat must produce bit-exact identical state across machines for
 * server-side replay validation (anti-cheat). Floating-point arithmetic does
 * NOT guarantee this — different CPUs, JIT implementations, and even compiler
 * optimizations can produce different last-bit results from the same inputs.
 *
 * Fixed-point math expresses fractional values as scaled integers. Every
 * operation is integer-only. Identical inputs produce identical outputs on
 * every machine, every browser, every Node version. Phase A's RNG bit-exact
 * preservation property test depends on this discipline.
 *
 * # The convention
 *
 * `FIXED_POINT_SCALE = 1000` — three decimal places of precision.
 *
 *   - A fixed-point value of `1000` represents 1.000
 *   - A fixed-point value of `12345` represents 12.345
 *   - A fixed-point value of `-500` represents -0.500
 *
 * Most game-side quantities (positions, velocities, HP, damage, durations)
 * fit comfortably in this representation. Three decimal places is more than
 * enough for sub-pixel positioning at typical tile sizes.
 *
 * # When to use which function
 *
 *   - `fp(x)`: convert a float to fixed-point. **Construction time only.**
 *     Never call this inside the combat reducer with non-constant inputs.
 *   - `fpToFloat(x)`: convert a fixed-point value to a float. **Render-side
 *     only.** Never call this inside the combat reducer.
 *   - `fpAdd` / `fpSub`: not provided. Just use `+` and `-`. Fixed-point
 *     addition is identical to integer addition.
 *   - `fpMul(a, b)`: multiply two fixed-point values. Returns fixed-point.
 *   - `fpDiv(a, b)`: divide two fixed-point values. Returns fixed-point.
 *   - `fpSqrt(x)`: integer square root of a fixed-point value. Returns
 *     fixed-point. Bit-exact across all machines.
 *
 * # Hard rules (enforced by static-analysis test)
 *
 *   1. NO `Math.sin`, `Math.cos`, `Math.tan`, `Math.atan`, `Math.atan2`,
 *      `Math.acos`, `Math.asin`, `Math.sqrt`, `Math.pow`, `Math.log`,
 *      `Math.exp` of non-constant arguments inside `packages/core/src/combat/`
 *      reducer code. Trigonometry uses lookup tables; sqrt uses `fpSqrt`.
 *   2. NO numeric literals containing a decimal point inside reducer code.
 *      Use `fp(0.5)` if you absolutely need a constant fractional value, and
 *      treat the result as a fixed-point integer thereafter.
 *   3. NO `Number.parseFloat`, `parseFloat`, or implicit float coercion via
 *      string concatenation followed by parsing.
 *
 * # Overflow safety
 *
 * JavaScript's `Number.MAX_SAFE_INTEGER` is 2^53 - 1 ≈ 9.007 × 10^15. With
 * `FIXED_POINT_SCALE = 1000`, intermediate products in `fpMul(a, b)` reach
 * `a * b` before division — meaning each operand must stay below ~2^26
 * (≈ 67 million in fixed-point, or ≈ 67 thousand in logical units) for
 * `fpMul` to remain exact.
 *
 * For typical combat values (positions in tiles, HP in thousands, damage in
 * hundreds), this leaves several orders of magnitude of headroom. If late-game
 * prestige values exceed this range, those quantities should be tracked
 * outside fixed-point math (e.g. `break_eternity.js` Decimal for currency).
 *
 * @module
 */

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The scale factor that maps logical units to fixed-point integers.
 *
 * Three decimal places of precision: a fixed-point value of `1000` represents
 * the logical value `1.0`. Cannot be changed without breaking save
 * compatibility — fixed-point values are persisted in saves.
 */
export const FIXED_POINT_SCALE = 1000;

/** Fixed-point representation of `0.5`. Useful for rounding. */
export const FP_HALF = 500;

/** Fixed-point representation of `1.0`. */
export const FP_ONE = 1000;

/** Fixed-point representation of `2.0`. */
export const FP_TWO = 2000;

/**
 * Maximum operand value for `fpMul` to remain exact under JS safe-integer
 * arithmetic. Operands beyond this may produce incorrect (rounded) results.
 *
 * Derivation: `fpMul(a, b)` computes `(a * b) / FIXED_POINT_SCALE`. The
 * intermediate product `a * b` must fit in `Number.MAX_SAFE_INTEGER`
 * (2^53 - 1). If both operands are bounded by sqrt(MAX_SAFE_INTEGER), the
 * product is safe. sqrt(2^53 - 1) ≈ 94906265 ≈ 2^26.5.
 */
export const FP_SAFE_MUL_OPERAND_MAX = 94_906_265;

// ─────────────────────────────────────────────────────────────────────────────
// Construction & extraction (boundary functions only)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Convert a JavaScript number to a fixed-point integer.
 *
 * **Boundary function only.** Do not call inside the combat reducer with
 * non-constant arguments — that introduces float arithmetic into the
 * deterministic simulation path.
 *
 * Use cases:
 *   - Defining constants at module load time (`const SPEED = fp(5.5)`)
 *   - Converting user input from menu UI (e.g., a settings slider)
 *   - Initial state construction
 *
 * @param value Floating-point logical value
 * @returns Fixed-point integer representation, rounded to nearest
 * @throws RangeError if `value` is `NaN`, `Infinity`, or `-Infinity`
 */
export function fp(value: number): number {
  if (!Number.isFinite(value)) {
    throw new RangeError(`fp: value must be finite, got ${value}`);
  }
  // Math.round handles negative values correctly (rounds half-away-from-zero
  // for our purposes; the slight asymmetry at exact half-values is acceptable
  // because fp() is a boundary function, not used inside the reducer).
  return Math.round(value * FIXED_POINT_SCALE);
}

/**
 * Convert a fixed-point integer to a JavaScript floating-point number.
 *
 * **Render-side only.** This function is the only legitimate exit from the
 * fixed-point world. Use it when feeding values to a renderer (PixiJS sprite
 * positions, etc.). NEVER call this inside the combat reducer.
 *
 * @param fpValue Fixed-point integer
 * @returns Floating-point logical value
 */
export function fpToFloat(fpValue: number): number {
  return fpValue / FIXED_POINT_SCALE;
}

// ─────────────────────────────────────────────────────────────────────────────
// Arithmetic
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Multiply two fixed-point integers, returning a fixed-point integer.
 *
 * Mathematically: `fpMul(fp(a), fp(b)) === fp(a * b)` (within rounding).
 *
 * Computes `(a * b + FP_HALF) / FIXED_POINT_SCALE` with integer truncation,
 * yielding round-half-up behavior for positive results. This matches
 * `Math.round` semantics for non-negative values.
 *
 * For negative results, the round-half-up bias is replaced with
 * round-half-toward-positive-infinity, which is consistent across machines
 * and acceptable for combat math (we don't depend on banker's rounding).
 *
 * **Overflow:** Both operands must satisfy `|a|, |b| <= FP_SAFE_MUL_OPERAND_MAX`
 * for the result to be exact. Outside this range, the intermediate product
 * exceeds `Number.MAX_SAFE_INTEGER` and accuracy degrades. This is checked
 * in dev (assertion) but skipped in production for performance.
 *
 * @param a First fixed-point operand
 * @param b Second fixed-point operand
 * @returns Fixed-point product, rounded to nearest integer
 */
export function fpMul(a: number, b: number): number {
  // The product `a * b` is the fixed-point value of the actual product
  // multiplied by FIXED_POINT_SCALE^2 (because each operand is already scaled
  // by FIXED_POINT_SCALE). Divide by FIXED_POINT_SCALE once to get back to
  // a single FIXED_POINT_SCALE factor.
  //
  // Add FP_HALF before dividing for rounding (round-half-up for positives,
  // round-half-toward-+infinity for negatives — deterministic either way).
  const product = a * b;
  if (product >= 0) {
    return Math.trunc((product + FP_HALF) / FIXED_POINT_SCALE);
  }
  return Math.trunc((product - FP_HALF) / FIXED_POINT_SCALE);
}

/**
 * Divide two fixed-point integers, returning a fixed-point integer.
 *
 * Mathematically: `fpDiv(fp(a), fp(b)) === fp(a / b)` (within rounding).
 *
 * Computes `(a * FIXED_POINT_SCALE) / b` with integer truncation toward zero.
 *
 * **Division by zero** throws — combat reducer code must guard against zero
 * divisors explicitly. Silent NaN propagation would break determinism.
 *
 * **Overflow:** The numerator `a * FIXED_POINT_SCALE` must fit in safe-integer
 * range. With `FIXED_POINT_SCALE = 1000`, this means `|a| <= 2^53 / 1000 ≈
 * 9 × 10^12`, which is far beyond any reasonable combat value.
 *
 * @param a Numerator (fixed-point)
 * @param b Denominator (fixed-point)
 * @returns Fixed-point quotient
 * @throws RangeError if `b === 0`
 */
export function fpDiv(a: number, b: number): number {
  if (b === 0) {
    throw new RangeError('fpDiv: division by zero');
  }
  // Scale numerator up before dividing to preserve fractional precision.
  // The scaled numerator (a * FIXED_POINT_SCALE) divided by b yields a result
  // already in fixed-point representation.
  //
  // Math.trunc gives round-toward-zero, which is deterministic and consistent
  // across machines. We do not add FP_HALF here because doing so would skew
  // results in a way inconsistent with Math.round semantics for division.
  // Fixed-point division is fundamentally lossy; we accept truncation.
  const numerator = a * FIXED_POINT_SCALE;
  return Math.trunc(numerator / b);
}

// ─────────────────────────────────────────────────────────────────────────────
// Square root (integer Newton's method, bit-exact)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Integer square root of a non-negative integer.
 *
 * Computes `floor(sqrt(n))` exactly, using Newton's method on integers.
 * The result is identical on every machine, every Node version, every
 * browser — no floating-point operations involved.
 *
 * Algorithm: classic Newton's method for square root.
 *   - Initial guess: `n` itself (safe upper bound)
 *   - Iteration: `next = (current + n / current) / 2` (integer division)
 *   - Termination: when `next >= current` (we've converged or overshot)
 *
 * Convergence is quadratic: ~O(log log n) iterations, typically 4-7 iterations
 * for any 32-bit-range input. Very fast.
 *
 * @param n Non-negative integer
 * @returns `floor(sqrt(n))` as an integer
 * @throws RangeError if `n < 0` or `n` is not an integer or not finite
 */
export function isqrt(n: number): number {
  if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0) {
    throw new RangeError(`isqrt: input must be a non-negative integer, got ${n}`);
  }
  if (n < 2) return n;

  // Newton's method on integers. Initial guess is `n` (a safe upper bound on
  // sqrt(n) for any n >= 2). Each iteration halves the error roughly.
  let x = n;
  let y = Math.trunc((x + 1) / 2);
  while (y < x) {
    x = y;
    y = Math.trunc((x + Math.trunc(n / x)) / 2);
  }
  // x now equals floor(sqrt(n))
  return x;
}

/**
 * Square root of a fixed-point value, returning a fixed-point value.
 *
 * Mathematically: `fpSqrt(fp(x)) === fp(sqrt(x))` (within rounding).
 *
 * Implementation: scale up by `FIXED_POINT_SCALE` before taking integer sqrt.
 * This works because:
 *   - Input is `x * FIXED_POINT_SCALE` (fixed-point representation)
 *   - We want output to be `sqrt(x) * FIXED_POINT_SCALE`
 *   - `sqrt(x * FIXED_POINT_SCALE * FIXED_POINT_SCALE) = sqrt(x) * FIXED_POINT_SCALE`
 *   - So `isqrt(fpValue * FIXED_POINT_SCALE)` gives us the right answer
 *
 * **Overflow:** The intermediate `fpValue * FIXED_POINT_SCALE` must fit in
 * safe-integer range. With `FIXED_POINT_SCALE = 1000`, this means
 * `fpValue <= Number.MAX_SAFE_INTEGER / 1000 ≈ 9 × 10^12`, leaving plenty
 * of headroom for any combat value.
 *
 * @param fpValue Fixed-point input (must be non-negative)
 * @returns Fixed-point square root, rounded down (floor)
 * @throws RangeError if `fpValue < 0` or not a finite integer
 */
export function fpSqrt(fpValue: number): number {
  if (!Number.isFinite(fpValue) || !Number.isInteger(fpValue) || fpValue < 0) {
    throw new RangeError(`fpSqrt: input must be a non-negative integer, got ${fpValue}`);
  }
  // Scale up to preserve precision in the square root. Without this scaling,
  // `isqrt(fp(2.0)) === isqrt(2000) === 44`, which represents 0.044 — wrong.
  // Scaling first: `isqrt(2000 * 1000) === isqrt(2_000_000) === 1414`, which
  // represents 1.414 — correct.
  return isqrt(fpValue * FIXED_POINT_SCALE);
}

// ─────────────────────────────────────────────────────────────────────────────
// Vector operations (fixed-point 2D)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Squared Euclidean distance between two fixed-point 2D points.
 *
 * **Prefer this over `fpDistance` for comparison** — it avoids the sqrt cost
 * and is exact (no rounding). Common pattern: `fpDistanceSquared(a, b) <
 * fpMul(radius, radius)` to test "within radius" without computing the actual
 * distance.
 *
 * Returns a fixed-point value: distance-squared in fixed-point. The magnitude
 * of this value can be larger than typical fixed-point values (it's squared),
 * so be aware of overflow. For combat-scale positions, comfortable.
 *
 * @returns Squared distance, fixed-point
 */
export function fpDistanceSquared(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
): number {
  const dx = x1 - x2;
  const dy = y1 - y2;
  // dx*dx and dy*dy are both fp-squared values; we use fpMul to get back to
  // fixed-point representation. fpMul handles the scaling correctly.
  return fpMul(dx, dx) + fpMul(dy, dy);
}

/**
 * Euclidean distance between two fixed-point 2D points.
 *
 * Uses `fpSqrt` internally — bit-exact across machines but more expensive
 * than `fpDistanceSquared`. Use this only when you need the actual distance
 * value (e.g., for damage falloff, range UI display).
 *
 * @returns Distance, fixed-point
 */
export function fpDistance(x1: number, y1: number, x2: number, y2: number): number {
  return fpSqrt(fpDistanceSquared(x1, y1, x2, y2));
}

/**
 * Length (magnitude) of a fixed-point 2D vector.
 *
 * Equivalent to `fpDistance(0, 0, x, y)`.
 *
 * @returns Vector length, fixed-point
 */
export function fpLength(x: number, y: number): number {
  return fpSqrt(fpMul(x, x) + fpMul(y, y));
}

/**
 * Normalize a fixed-point 2D vector to unit length.
 *
 * Returns `{ x: 0, y: 0 }` for the zero vector (which has no defined
 * direction). Combat reducer code should handle the zero-vector case
 * explicitly when direction matters.
 *
 * Result vector has approximately fixed-point length `FP_ONE` (1.0), within
 * the precision of integer sqrt — typically off by at most 1 fixed-point
 * unit (0.001 logical) due to truncation in `fpSqrt`.
 *
 * @returns Normalized vector with components in fixed-point
 */
export function fpNormalize(x: number, y: number): { x: number; y: number } {
  const length = fpLength(x, y);
  if (length === 0) {
    return { x: 0, y: 0 };
  }
  return {
    x: fpDiv(x, length),
    y: fpDiv(y, length),
  };
}
